import React, { useContext } from 'react';
import styled from 'styled-components';
import { useWindowSize } from 'hooks';
import { AxLogo, DaiichiSankyo, CongressFeedbackProgram } from 'assets/svgs';
import { useLocation } from '@reach/router';
import { FirebaseContext } from 'context';

function Links({ user, isCanadian }) {
  return (
    <LinksContainer>
      {user ? (
        isCanadian ? (
          <>
            <a
              href="https://www.daiichi-sankyo.ca/terms-of-use/"
              target="_blank"
              rel="noreferrer">
              Terms of Use
            </a>
            <a
              href="https://www.daiichi-sankyo.ca/data-protection/"
              target="_blank"
              rel="noreferrer">
              Data Protection
            </a>
            <a
              href="https://www.daiichi-sankyo.ca/imprint/"
              target="_blank"
              rel="noreferrer">
              Imprint
            </a>
          </>
        ) : (
          <>
            <a
              href="https://www.daiichi-sankyo.ie/disclaimer/"
              target="_blank"
              rel="noreferrer">
              Daiichi Sankyo IE Disclaimer
            </a>
            <a
              href="https://www.daiichi-sankyo.ie/data-protection/"
              target="_blank"
              rel="noreferrer">
              Daiichi Sankyo IE Data Protection
            </a>
            <a
              href="https://www.daiichi-sankyo.ie/imprint/"
              target="_blank"
              rel="noreferrer">
              Daiichi Sankyo IE Imprint
            </a>
          </>
        )
      ) : (
        <p>
          Full Terms & Conditions, Data Protection and Cookies Policy available
          after login.
        </p>
      )}
    </LinksContainer>
  );
}

function Footer() {
  const { width } = useWindowSize();
  const { user } = useContext(FirebaseContext);
  const location = useLocation();
  const isCanadian = location?.pathname?.includes('/sessions/ae9zii/');
  return (
    <>
      <Divider />
      <Container>
        <Logos alternate={user && width < 1200}>
          {user && width < 1200 ? (
            <div>
              <a href="/" target="_blank" rel="noreferrer">
                <StyledCfpLogo />
              </a>
            </div>
          ) : (
            <div>
              <a
                href="https://www.daiichi-sankyo.ie/"
                target="_blank"
                rel="noreferrer">
                <StyledDsLogo />
              </a>
            </div>
          )}
          <div>
            <p>Produced by</p>
            <a href="https://www.agencyx.ie/" target="_blank" rel="noreferrer">
              <StyledAxLogo />
            </a>
            <p>
              This is a non-promotional, Congress Feedback Platform,
              incorporating a series of discussion topics, organised and funded
              by Daiichi Sankyo.
              <br />
              The platform on which this Congress Feedback Platform is housed is
              designed and developed by Agency X.
              <br />
              The information provided here includes details of indications that
              may be off-label and are for scientific medical exchange purposes
              only. Daiichi Sankyo does not, under any circumstances, promote
              its products for off-label or unapproved uses. This information
              reflects investigational compounds and/or investigational uses of
              approved products not approved by Health Canada for use in Canada.
              The safety and efficacy of these investigational agents or
              investigational uses of approved products have not been
              established. Any approved products should be used in accordance
              with their product labeling (Product Monograph).
            </p>
            {user && width < 1200 && (
              <div>
                <a
                  href="https://www.daiichi-sankyo.ie/"
                  target="_blank"
                  rel="noreferrer">
                  <StyledDsLogo />
                </a>
              </div>
            )}
          </div>
        </Logos>
        {width >= 1200 && (
          <DesktopSmallPrint>
            This is a non-promotional, Congress Feedback Platform, incorporating
            a series of discussion topics, organised and funded by Daiichi
            Sankyo.
            <br />
            The platform on which this Congress Feedback Platform is housed is
            designed and developed by Agency X.
            <br />
            The information provided here includes details of indications that
            may be off-label and are for scientific medical exchange purposes
            only. Daiichi Sankyo does not, under any circumstances, promote its
            products for off-label or unapproved uses. This information reflects
            investigational compounds and/or investigational uses of approved
            products not approved by Health Canada for use in Canada. The safety
            and efficacy of these investigational agents or investigational uses
            of approved products have not been established. Any approved
            products should be used in accordance with their product labeling
            (Product Monograph).
          </DesktopSmallPrint>
        )}
        <Copywright>
          <div>
            <p>Copyright © 2026 - Designed and Developed by Agency X</p>
            <p>Date of Preparation: August 2026</p>
          </div>
          <Links user={user} isCanadian={isCanadian} />
        </Copywright>
      </Container>
      {width >= 1200 && (
        <DesktopCopywright>
          <div>
            <div>
              <p>Copyright © 2026 - Designed and Developed by Agency X</p>
              <p>
                {/* Job Code xxxxxxx &nbsp;&nbsp;·&nbsp;&nbsp; */}Date of
                Preparation: August 2026
              </p>
            </div>
            <Links user={user} isCanadian={isCanadian} />
          </div>
        </DesktopCopywright>
      )}
    </>
  );
}

const Container = styled.footer`
  position: relative;

  @media (min-width: 1200px) {
    align-items: start;
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-column-gap: 1.25rem;
    margin: 0 auto;
    max-width: 1385px;
  }
`;

const Divider = styled.div`
  background-color: var(--light-grey);
  height: 0.063rem;
  left: 0.625rem;
  position: relative;
  width: calc(100% - 1.25rem);
  z-index: -1;
`;

const Logos = styled.div`
  padding: 0.875rem 1.25rem;

  svg {
    width: 100%;
    height: auto;
  }

  > div:first-of-type {
    display: flex;
    gap: 20px;
    justify-content: ${({ alternate }) =>
      alternate ? 'center' : 'space-between'};
    margin-bottom: 1.4rem;
    max-width: ${({ alternate }) => (alternate ? '100%' : '20.938rem')};
    height: auto;
    width: 100%;

    a:first-of-type {
      position: relative;
      top: 0.625rem;
    }

    @media (min-width: 768px) {
      max-width: ${({ alternate }) => (alternate ? '100%' : '560px')};

      a:first-of-type {
        position: relative;
        top: 1rem;
      }
    }
  }

  > div:last-of-type {
    p {
      font-size: 0.75rem;

      &:last-of-type {
        letter-spacing: -0.12px;
        line-height: 140%;
        margin-bottom: ${({ alternate }) => (alternate ? '1.5rem' : '0.3rem')};
        margin-top: 1.05rem;
        max-width: 610px;
      }
    }
  }

  @media (min-width: 1200px) {
    display: flex;
    justify-content: space-between;
    grid-column: 1/-1;
    grid-row: 1/2;
    padding: 2.45rem 0 0.75rem;

    > div:last-of-type {
      p {
        font-size: 1rem;
        margin-top: 0.4em;
      }

      p:last-of-type {
        display: none;
      }
    }
  }
`;

const StyledDsLogo = styled(DaiichiSankyo)`
  max-width: 9.563rem;

  @media (min-width: 768px) {
    max-width: 16rem;
  }
`;

const StyledAxLogo = styled(AxLogo)`
  margin-top: 0.8rem;
  max-width: 7.563rem;

  @media (min-width: 768px) {
    max-width: 10.813rem;
  }

  @media (min-width: 1200px) {
    margin-top: 1.05rem;
  }
`;

const StyledCfpLogo = styled(CongressFeedbackProgram)`
  margin: 0.5rem auto 1rem auto;
  width: 100%;
`;

const DesktopSmallPrint = styled.p`
  font-size: 14px;
  grid-column: 1/-1;
  grid-row: 2/3;
  letter-spacing: -0.12px;
  line-height: 140%;
  margin-bottom: 1.55rem;
`;

const Copywright = styled.div`
  background: #fff;
  font-size: 0.75rem;
  line-height: 1.429em;
  padding: 0.75rem 1.25rem;

  @media (min-width: 1200px) {
    display: none;
  }
`;

const DesktopCopywright = styled.div`
  background: #fff;
  font-size: 0.75rem;
  line-height: 1.429em;
  padding: 0.75rem 0;

  > div {
    display: grid;
    grid-column-gap: 1.25rem;
    grid-template-columns: repeat(12, 1fr);
    margin: 0 auto;
    max-width: 1385px;

    > div:nth-of-type(1) {
      display: flex;
      grid-column: 1/-5;
      grid-row: 1/2;
      justify-content: space-between;
      margin-right: 7.5rem;

      p:nth-of-type(1) {
        margin-right: 3.5rem;
      }
    }
  }
`;

const LinksContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.325rem 0.75rem;
  grid-column: 1/-1;
  grid-row: 2/3;
  margin-top: 1rem;

  a {
    color: var(--brown);
    font-size: 0.75rem;
    text-decoration: underline;
    &:not(:first-of-type) {
      border-left: 1px solid var(--brown);
      padding-left: 0.75rem;
    }
  }

  a:hover,
  a:active,
  a:focus {
    color: var(--brown);
  }
`;

export default Footer;
